import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useTenantsStore } from '@/stores';
import type ISite from "@/models/responses/ISite";
import CreateSiteRequest from "@/models/requests/CreateSiteRequest";
import UpdateSiteRequest from "@/models/requests/UpdateSiteRequest";
import Site from "@/models/frontend/Site";

export const useSitesStore = defineStore("sites", {
  state: () => ({
    sites: [] as Site[],
    error: "" as any,
    loaded: false,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loaded) return;

      this.sites = [];
      this.error = "";
      await api
        .get<ISite[]>("v1.0/sites")
        .then((response) => {
          this.sites = response.data.map(this.mapToFrontend);
          this.loaded = true;
        })
        .catch((error) => (this.error = error));
    },
    async create(site: CreateSiteRequest) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<ISite>("v1.0/sites", site)
        .then((response) => this.sites.push(this.mapToFrontend(response.data)))
        .catch((error) => (this.error = error));
    },
    async update(siteId: number, site: UpdateSiteRequest) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .put<ISite>(`v1.0/sites/${siteId}`, site)
        .then((response) => {
          const index = this.sites.findIndex((t) => t.id === siteId);
          this.sites[index] = this.mapToFrontend(response.data);
        })
        .catch((error) => (this.error = error));
    },
    async delete(siteId: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .delete(`v1.0/sites/${siteId}`)
        .then(() => {
          this.sites = this.sites.filter((t) => t.id !== siteId);
        })
        .catch((error) => (this.error = error));
    },
    mapToFrontend(site: ISite) {
      const tenantsStore = useTenantsStore();
      const tenant = tenantsStore.getById(site.tenantId);
      return new Site(site.id, tenant, site.code, site.name, site.info);
    },
  },
  getters: {
    getById: (state) => (siteId: number): Site => {
      return <Site>state.sites.find((s) => s.id === siteId);
    },
    getByCode: (state) => (code: string): Site => {
      return <Site>state.sites.find((s) => s.code === code);
    },
    getByTenantId: (state) => (tenantId: number): Site[] => {
      return state.sites.filter((s) => s.tenant.id === tenantId);
    },
    getAll: (state) => (): Site[] => {
      return state.sites;
    },
  },
});
